import colors from "common/constants/colorsOld";
import { LW_HATCH, LW_PERSP, WALL_CUT_STROKE_WIDTH } from "doc/plans/constants";
import type { StyleSheet, StyleSheetBase } from "doc/plans/planStyles";
import { styleSheetMerge, styleSheetNew } from "doc/plans/planStyles";
import type { RenderedPerspectiveLines } from "./RenderedPerspectiveLines";

export interface Linework3DLayer<T extends StyleSheetBase> {
  readonly source: string & keyof RenderedPerspectiveLines;
  readonly style?: string & keyof T;
}

export interface Linework3DRenderingConfig<T extends StyleSheetBase> {
  readonly styles: StyleSheet<T>;
  readonly layers: Linework3DLayer<T>[];
}

export function linework3DRenderingConfigNew<T extends StyleSheetBase>({
  styles,
  layers,
}: {
  styles: StyleSheet<T>;
  layers: Linework3DLayer<T>[];
}): Linework3DRenderingConfig<T> {
  return {
    styles,
    layers,
  };
}

export const defaultLinework3DConfig = linework3DRenderingConfigNew({
  styles: styleSheetNew({
    solid: {
      linewidth: LW_PERSP,
    },
    hatch: {
      linewidth: LW_HATCH,
      strokeColor: colors.stoneDark,
    },
  }),
  layers: [
    {
      source: "groundHatchLines",
      style: "hatch",
    },
    {
      source: "groundLines",
      style: "solid",
    },
    {
      source: "aboveGroundHatchLines",
      style: "hatch",
    },
    {
      source: "aboveGroundLines",
      style: "solid",
    },
  ],
});

export const defaultSectionLinework3DConfig = linework3DRenderingConfigNew({
  styles: styleSheetMerge(defaultLinework3DConfig.styles, {
    section: {
      linewidth: WALL_CUT_STROKE_WIDTH,
    },
  }),
  layers: [
    {
      source: "belowGroundHatchLines",
      style: "hatch",
    },
    {
      source: "belowGroundLines",
      style: "solid",
    },
    {
      source: "groundHatchLines",
      style: "hatch",
    },
    {
      source: "groundLines",
      style: "solid",
    },
    {
      source: "aboveGroundHatchLines",
      style: "hatch",
    },
    {
      source: "aboveGroundLines",
      style: "solid",
    },
    // Hatching is disabled in section views until we have a complete solution, so the
    // "crossSectionHatchLines" source is intentionally not drawn. This keeps PDF/vector output
    // consistent with the three.js preview, which also renders sections without hatching.
    // TODO (SDO-1131): Implement section view hatching.
    {
      source: "crossSectionLines",
      style: "section",
    },
  ],
});
